import { useEffect, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import {
  LoginForm,
  LoginSelectApplicationForm,
  LoginSelectClientForm,
  LoginSelectSiteForm,
  SSOClientForm,
} from 'auth/components'
import { AuthLoginStep, IAuthorizedUser, TJwtPayload } from 'auth/models'
import { setLoginStep, setUser } from 'auth/store'
import {
  cleanupOAuthState,
  exchangeCodeForTokens,
  redirectToSSOError,
  verifyOAuthState,
} from 'auth/utils'
import { fromPayloadToUserMapper } from 'auth/utils'
import { useDispatch, useSelector } from 'common/hooks'
import { jwtDecode } from 'jwt-decode'
import { RoutesEnum } from 'services'

export default function LoginPage() {
  const { t } = useTranslation()
  const { loginStep } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const location = useLocation()

  // Track if SSO callback was already processed for this authorization code
  const processedCodeRef = useRef<string | null>(null)

  // Handle SSO callback if OAuth parameters are present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const code = urlParams.get('code')
    const state = urlParams.get('state')
    const error = urlParams.get('error')

    // Only process if this looks like an OAuth callback AND we're still in SIGN_IN step AND we haven't processed this code yet
    if (
      (code || state || error) &&
      loginStep === AuthLoginStep.SIGN_IN &&
      code !== processedCodeRef.current
    ) {
      // Mark this code as being processed
      if (code) {
        processedCodeRef.current = code
      }

      // Clean URL immediately to prevent re-processing
      window.history.replaceState({}, document.title, '/login')

      const handleSSOCallback = async () => {
        try {
          // Handle OAuth errors
          if (error) {
            const errorDescription = urlParams.get('error_description')
            const organization = urlParams.get('organization')
            redirectToSSOError(
              error,
              errorDescription || undefined,
              organization || undefined,
            )
            return
          }

          // Verify state parameter (CSRF protection)
          if (!state) {
            redirectToSSOError('invalid_state', 'Missing state parameter')
            return
          }

          const stateVerificationResult = verifyOAuthState(state)

          if (!stateVerificationResult) {
            redirectToSSOError('invalid_state', 'Invalid state parameter')
            return
          }

          if (code) {
            const redirectUri = `${window.location.origin}/login`

            // Exchange code for tokens
            const tokens = await exchangeCodeForTokens(code, redirectUri)

            // Store tokens in localStorage
            localStorage.setItem('access_token', tokens.access_token)
            if (tokens.id_token) {
              localStorage.setItem('id_token', tokens.id_token)
            }

            // Decode token to get user info
            const decodedToken = jwtDecode(
              tokens.access_token,
            ) as TJwtPayload<IAuthorizedUser>
            localStorage.setItem('user_id', decodedToken.userId)
            // Update auth state
            const mappedUser = fromPayloadToUserMapper(decodedToken)
            dispatch(setUser({ user: mappedUser }))
            dispatch(setLoginStep({ step: AuthLoginStep.SELECT_CLIENT }))

            // Clean up OAuth state
            cleanupOAuthState()
          }
        } catch (error) {
          cleanupOAuthState()
          redirectToSSOError(
            'token_exchange_failed',
            error instanceof Error ? error.message : 'Unknown error occurred',
          )
        }
      }

      handleSSOCallback()
    }
  }, [location.search, dispatch, loginStep])

  const renderContent = useMemo(() => {
    switch (loginStep) {
      case AuthLoginStep.SIGN_IN:
      default:
        return <LoginForm />
      case AuthLoginStep.SELECT_CLIENT:
        return <LoginSelectClientForm />
      case AuthLoginStep.SELECT_APPLICATION:
        return <LoginSelectApplicationForm />
      case AuthLoginStep.SELECT_SITE:
        return <LoginSelectSiteForm />
      case AuthLoginStep.SSO_CLIENT_INPUT:
        return <SSOClientForm />
      case AuthLoginStep.INDEX:
        return <Navigate to={RoutesEnum.HOME} />
    }
  }, [loginStep])

  return (
    <>
      <Helmet>
        <title>{t('auth.page.signIn')}</title>
      </Helmet>
      {renderContent}
    </>
  )
}
